import { Component, OnInit, signal, input, inject, output, OnDestroy } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';

import {
    LucideAngularModule,
    ChevronRight,
    LayoutDashboard,
    Users,
    ScrollText,
    Database,
    Cog,
    InfoIcon,
    CircleQuestionMark,
    ChartColumn,
    FilePenLine,
    MessageCircle,
    UserStar,
    CircleUser,
    FilePen,
    FileSpreadsheet,
    FileX,
    ShieldUser,
    UserSearch,
    ArrowRightLeft,
    UserPen,
    Ban,
    Flag,
    FileCheck,
    ListChecks,
    List,
    ListTree,
    Bell,
    House,
    Logs,
    Handshake,
    Building,
} from 'lucide-angular';
import { Router, RouterLink, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, skip, take, takeUntil } from 'rxjs/operators';
import { AuthService } from '@app/core/api/auth/auth.service';
import { ChatService } from '@app/core/api/chat/chat.service';
import { EchoService } from '@app/core/services/echo.service';

import { UserType, getUpperUserType } from '@app/enums/UserType.enum';
import { InvestService } from '@app/core/api/users/invest.service';

export interface SidebarMenuItem {
    label: string;
    canAccess?: boolean;
    route?: string;
    icon?: any;
    active: boolean;
    open?: boolean;
    badge?: number;
    children?: SidebarMenuItem[];
    state?: Record<string, any>;
    matchRoutes?: string[];
}

@Component({
    selector: 'app-sidebar',
    standalone: true,
    imports: [NgTemplateOutlet, LucideAngularModule, RouterLink],
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
    opened = input(false);
    isMobile = input(false);
    transitionsEnabled = signal(false);
    closeSidebar = output<void>();
    router = inject(Router);

    authService = inject(AuthService);
    investService = inject(InvestService);
    chatService = inject(ChatService);
    echoService = inject(EchoService);

    unreadChatCount = signal(0);
    private previousUrl: string = '';
    private userChannel: any = null;
    private destroy$ = new Subject<void>();

    readonly currentUser = this.authService.user();

    readonly ChevronRight = ChevronRight;
    readonly LayoutDashboard = LayoutDashboard;
    readonly Users = Users;
    readonly ScrollText = ScrollText;
    readonly Database = Database;
    readonly Cog = Cog;
    readonly InfoIcon = InfoIcon;
    readonly ChartColumn = ChartColumn;
    readonly FilePenLine = FilePenLine;
    readonly MessageCircle = MessageCircle;
    readonly UserStar = UserStar;
    readonly CircleUser = CircleUser;
    readonly FilePen = FilePen;
    readonly FileX = FileX;
    readonly ShieldUser = ShieldUser;
    readonly UserSearch = UserSearch;
    readonly ArrowRightLeft = ArrowRightLeft;
    readonly UserPen = UserPen;
    readonly Ban = Ban;
    readonly Flag = Flag;
    readonly FileCheck = FileCheck;
    readonly ListChecks = ListChecks;
    readonly List = List;
    readonly ListTree = ListTree;
    readonly Bell = Bell;
    readonly House = House;
    readonly Logs = Logs;

    internalMenuItems = signal<SidebarMenuItem[]>([
        {
            label: 'Home',
            active: false,
            canAccess: true,
            route: '/home',
            icon: House,
            children: [],
        },
        // {
        //     label: 'Dashboard',
        //     active: false,
        //     canAccess: this.canAccess('view dashboard'),
        //     route: '/dashboard',
        //     icon: LayoutDashboard,
        //     children: [],
        // },
        // {
        //     label: 'My Approvals',
        //     active: false,
        //     canAccess:
        //         this.canAccess('view Investor-Investee-Application-Endorser') ||
        //         this.canAccess('view Investor-Investee-Application-Approver') ||
        //         this.canAccess('view Investor-Investee-Account-Update-Endorser') ||
        //         this.canAccess('view Investor-Investee-Account-Update-Approver') ||
        //         this.canAccess('view Voluntary-Delisting-Endorser-Approval') ||
        //         this.canAccess('view Voluntary-Delisting-Approver-Approval') ||
        //         this.canAccess('view Involuntary-Delisting-Endorser-Approval') ||
        //         this.canAccess('view Involuntary-Delisting-Approver-Approval'),
        //     icon: FilePenLine,
        //     children: [
        // {
        //     label: 'Investor/Investee Application',
        //     route: '/application-registration',
        //     canAccess:
        //         this.canAccess('view Investor-Investee-Application-Endorser') ||
        //         this.canAccess('view Investor-Investee-Application-Approver'),
        //     icon: FilePen,
        //     active: false,
        // },
        // {
        //     label: 'Account Details Update',
        //     route: '/account-update',
        //     canAccess:
        //         this.canAccess('view Investor-Investee-Account-Update-Endorser') ||
        //         this.canAccess('view Investor-Investee-Account-Update-Approver'),
        //     icon: FileSpreadsheet,
        //     active: false,
        // },
        // {
        //     label: 'Voluntary Delisting Requests',
        //     route: '/approvals/de-listing',
        //     canAccess:
        //         this.canAccess('view Voluntary-Delisting-Endorser-Approval') ||
        //         this.canAccess('view Voluntary-Delisting-Approver-Approval'),
        //     icon: FileX,
        //     active: false,
        // },
        // {
        //     label: 'Involuntary Delisting Requests',
        //     route: '/approvals/involuntary-de-listing',
        //     canAccess:
        //         this.canAccess('view Involuntary-Delisting-Endorser-Approval') ||
        //         this.canAccess('view Involuntary-Delisting-Approver-Approval'),
        //     icon: FileX,
        //     active: false,
        // },
        //     ],
        //     open: false,
        // },
        // {
        //     label: 'User Management',
        //     active: false,
        //     canAccess:
        //         this.canAccess('view manage-users') ||
        //         this.canAccess('view manage-user-roles') ||
        //         this.canAccess('view External-Users-Management'),
        //     icon: Users,
        //     children: [
        // {
        //     label: 'Internal Users Management',
        //     route: '/internal-users',
        //     canAccess: this.canAccess('view manage-users'),
        //     icon: UserSearch,
        //     active: false,
        // },
        // {
        //     label: 'Internal Role Management',
        //     route: '/internal-role-management',
        //     canAccess: this.canAccess('view manage-user-roles'),
        //     icon: ShieldUser,
        //     active: false,
        // },
        // {
        //     label: 'External Users Management',
        //     route: '/external-users',
        //     canAccess: this.canAccess('view External-Users-Management'),
        //     icon: UserSearch,
        //     active: false,
        // },
        //     ],
        //     open: false,
        // },
        // {
        //     label: 'Logs Analysis and Reports',
        //     route: '/logs-analysis-and-reports',
        //     canAccess: this.canAccess('view audit-trail'),
        //     active: false,
        //     icon: ChartColumn,
        //     children: [],
        // },
        // {
        //     label: 'Login Logs',
        //     route: '/login-logs',
        //     canAccess: this.canAccess('view login-logs'),
        //     active: false,
        //     icon: Logs,
        //     children: [],
        // },
        // {
        //     label: 'System Configuration',
        //     route: '/system-configuration',
        //     canAccess: this.canAccess('view config'),
        //     icon: Cog,
        //     active: false,
        //     children: [],
        // },
        // {
        //     label: 'Master Data Management',
        //     route: '/master-data-management',
        //     canAccess: this.canAccess('view manage-master-data'),
        //     icon: Database,
        //     active: false,
        //     children: [
        // {
        //     label: 'Documentary Requirements',
        //     route: '/documentary-requirements',
        //     canAccess: this.canAccess('view manage-master-data'),
        //     icon: FileCheck,
        //     active: false,
        //     children: [],
        // },
        // {
        //     label: 'Approval Status',
        //     route: '/approval-status',
        //     canAccess: this.canAccess('view manage-master-data'),
        //     icon: ListChecks,
        //     active: false,
        //     children: [],
        // },
        // {
        //     label: 'Banners',
        //     route: '/banners',
        //     canAccess: this.canAccess('view manage-master-data'),
        //     icon: Flag,
        //     active: false,
        //     children: [],
        // },
        // {
        //     label: 'Prohibited Words',
        //     route: '/prohibited-words',
        //     canAccess: this.canAccess('view manage-master-data'),
        //     icon: Ban,
        //     active: false,
        //     children: [],
        // },
        // {
        //     label: 'Notifications and Alerts',
        //     route: '/alerts',
        //     canAccess: this.canAccess('view manage-master-data'),
        //     icon: Bell,
        //     active: false,
        //     children: [],
        // },
        // {
        //     label: 'FAQs',
        //     route: '/manage-faqs',
        //     canAccess: this.canAccess('view manage-master-data'),
        //     icon: List,
        //     active: false,
        //     children: [],
        // },
        // {
        //     label: 'FAQ Categories',
        //     route: '/manage-faq-categories',
        //     canAccess: this.canAccess('view manage-master-data'),
        //     icon: ListTree,
        //     active: false,
        //     children: [],
        // },
        //     ],
        // },
    ]);

    externalMenuItems = signal<SidebarMenuItem[]>([
        {
            label: 'Home',
            active: false,
            canAccess: true,
            route: '/home',
            icon: House,
            children: [],
        },
        {
            label: 'Account Management',
            route: `/application-registration/${this.currentUser?.registration_application_id}/update`,
            canAccess: true,
            active: false,
            icon: UserPen,
            children: [],
        },
        {
            label: 'Investor/Investee Connect',
            route: '/investor-investee-connect',
            canAccess: this.hasRole('Registrant'),
            active: false,
            icon: Handshake,
            children: [],
            matchRoutes: ['/investor-investee-connect', '/connect-with-users', '/profile-narrative'],
        },
        {
            label: 'Public Profile Management',
            route: '/investor-investee-profile',
            canAccess: this.hasRole('Registrant'),
            active: false,
            icon: Building,
            children: [],
            state: {
                id: this.currentUser?.invest_id,
                code:
                    this.currentUser?.type == 'Investor'
                        ? this.currentUser?.registration_name
                        : this.currentUser?.invest_code,
                type: this.currentUser?.type,
            },
        },
        {
            label: 'Connection Requests',
            route: '/connection-requests',
            canAccess: this.hasRole('Registrant'),
            active: false,
            icon: ArrowRightLeft,
            children: [],
        },
        {
            label: 'Chats',
            route: '/chat',
            canAccess: this.hasRole('Registrant'),
            active: false,
            icon: MessageCircle,
            children: [],
        },
        {
            label: 'Activity Logs',
            route: '/activity-logs',
            canAccess: this.hasRole('Registrant'),
            active: false,
            icon: Logs,
            children: [],
        },
        {
            label: 'FAQs',
            route: '/help/faqs',
            canAccess: this.hasRole('Registrant'),
            active: false,
            icon: CircleQuestionMark,
            children: [],
        },
        {
            label: 'About Us',
            route: '/help/about-us',
            canAccess: this.hasRole('Registrant'),
            active: false,
            icon: InfoIcon,
            children: [],
        },
    ]);

    menuItems = signal<SidebarMenuItem[]>([]); // Will be set based on user type

    ngOnInit() {
        // Check if account is suspended - show limited menu
        if (this.authService.isAccountSuspended()) {
            this.menuItems = signal<SidebarMenuItem[]>([
                {
                    label: 'Account Suspended',
                    active: true,
                    canAccess: true,
                    route: '/account-suspended',
                    icon: Ban,
                    children: [],
                },
            ]);
        } else {
            this.menuItems = this.setMenuItemsByUserType(this.currentUser?.user_type);
        }

        // Load initial unread chat count
        this.loadUnreadChatCount();

        // Subscribe to Echo events for real-time updates
        this.subscribeToUserChannel();

        // When on /chat page, let the chat-sidebar drive the badge via shared service.
        // This covers messages read inside the chat page that the WebSocket listener skips.
        this.chatService.globalUnreadCount$
            .pipe(
                skip(1), // prevent initial 0 from wiping the API-loaded count
                takeUntil(this.destroy$),
            )
            .subscribe((count) => {
                if (this.router.url.includes('/chat')) {
                    this.unreadChatCount.set(count);
                    this.updateChatBadge(count);
                }
            });

        setTimeout(() => {
            this.transitionsEnabled.set(true);
        }, 0);

        this.markActiveRoute(this.menuItems());

        // AFTER
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this.destroy$),
            )
            .subscribe((event: NavigationEnd) => {
                const currentUrl = event.urlAfterRedirects;
                const wasOnChat = this.previousUrl.includes('/chat');
                const isOnChat = currentUrl.includes('/chat');

                this.markActiveRoute(this.menuItems());

                if (wasOnChat && !isOnChat) {
                    // Re-register listeners that chat components wiped on their destroy
                    const userId = this.currentUser?.id;
                    if (userId) {
                        this.setupUserChannel(userId);
                    }
                }

                this.previousUrl = currentUrl;
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.unsubscribeFromUserChannel();
    }

    private subscribeToUserChannel(): void {
        const userId = this.currentUser?.id;
        if (!userId) {
            console.warn('No user ID in sidebar, cannot subscribe to user channel');
            return;
        }

        // If Echo is not connected yet, wait for connection using observable
        if (!this.echoService.isConnected()) {
            // Subscribe to connection ready event
            this.echoService
                .connectionReady()
                .pipe(
                    filter((isConnected) => isConnected && !this.userChannel),
                    take(1),
                )
                .subscribe(() => {
                    this.setupUserChannel(userId);
                });
            return;
        }

        this.setupUserChannel(userId);
    }

    private setupUserChannel(userId: string): void {
        // Stop own listeners before re-registering to prevent duplicates
        if (this.userChannel) {
            this.userChannel.stopListening('.message.sent');
            this.userChannel.stopListening('.message.read');
        }

        // Subscribe to user's private channel for new messages
        this.userChannel = this.echoService.private(`user.${userId}`);

        if (this.userChannel) {
            console.log('[Sidebar] User channel connected for user:', userId);
            console.log('[Sidebar] Subscribed to user channel:', `user.${userId}`);

            // Listen for new messages and message reads to update badge
            this.userChannel.listen('.message.sent', (event: any) => {
                console.log('[Sidebar] Received message event:', event);

                // Increment unread count if message is not from current user and not on chat page
                const currentUserId = parseInt(userId);
                const messageSenderId = event.sender?.id
                    ? parseInt(String(event.sender.id))
                    : event.sender_id
                      ? parseInt(String(event.sender_id))
                      : null;
                const isFromCurrentUser = messageSenderId === currentUserId;
                const isOnChatPage = this.router.url.includes('/chat');

                console.log('[Sidebar] Message analysis:', {
                    isFromCurrentUser,
                    isOnChatPage,
                    senderId: messageSenderId,
                    currentUserId,
                    currentUrl: this.router.url,
                });

                if (!isFromCurrentUser && !isOnChatPage) {
                    console.log('Incrementing unread count in main sidebar');
                    this.unreadChatCount.update((count) => count + 1);
                    this.updateChatBadge(this.unreadChatCount());
                }
            });

            this.userChannel.listen('.message.read', (_event: any) => {
                // Reload the true count from the API — decrementing by 1 is wrong
                // because mark-all-read clears N messages in a single event.
                this.loadUnreadChatCount();
            });
        } else {
            console.error('Failed to subscribe to user channel in sidebar');
        }
    }

    private unsubscribeFromUserChannel(): void {
        if (this.userChannel) {
            const userId = this.currentUser?.id;
            if (userId) {
                this.echoService.leaveChannel(`user.${userId}`);
            }
            this.userChannel = null;
        }
    }

    loadUnreadChatCount() {
        this.chatService.getUnreadCount().subscribe({
            next: (response) => {
                this.unreadChatCount.set(response.unread_count);
                // Update the chat menu item badge
                this.updateChatBadge(response.unread_count);
            },
            error: (error) => {
                console.error('Error loading unread count:', error);
            },
        });
    }

    /**
     * Reset unread chat count (called when user enters chat page)
     */
    resetChatCounter() {
        console.log('Resetting chat counter in main sidebar');
        this.unreadChatCount.set(0);
        this.updateChatBadge(0);
    }

    updateChatBadge(count: number) {
        this.menuItems.update((items) => {
            return items.map((item) => {
                if (item.route === '/chat') {
                    return { ...item, badge: count > 0 ? count : undefined };
                }
                return item;
            });
        });
    }

    toggleAccordion(item: SidebarMenuItem) {
        item.open = !item.open;
        this.menuItems.update((items) => [...items]);

        // Optional: Close other accordions if you want only one open at a time
        // this.closeOtherAccordions(item, this.menuItems());
    }

    // Optional method to close other accordions (uncomment the call above to use)
    private closeOtherAccordions(currentItem: SidebarMenuItem, items: SidebarMenuItem[]) {
        for (const item of items) {
            if (item !== currentItem && item.children?.length) {
                item.open = false;
            }
            if (item.children?.length) {
                this.closeOtherAccordions(currentItem, item.children);
            }
        }
    }

    onMenuClick(item: SidebarMenuItem) {
        // Only allow menu items to be set as active
        if (!item.children?.length) {
            this.clearActive(this.menuItems());
            item.active = true;
            this.menuItems.update((items) => [...items]);

            if (item.state) {
                this.investService.setState({
                    id: item.state['id'],
                    code: item.state['code'],
                    type: item.state['type'],
                });
            }

            if (item.route) {
                this.router.navigate([item.route]);
            }

            // Close sidebar on mobile when clicking a menu item
            if (this.isMobile()) {
                this.closeSidebar.emit();
            }
        }
    }

    hasActiveChild(item: SidebarMenuItem): boolean {
        return item.children?.some((child) => child.active || this.hasActiveChild(child)) || false;
    }

    clearActive(items: SidebarMenuItem[]) {
        for (const i of items) {
            i.active = false;
            if (i.children?.length) {
                this.clearActive(i.children);
            }
        }
    }

    markActiveRoute(items: SidebarMenuItem[]) {
        const url = this.router.url;
        for (const i of items) {
            const matchesMainRoute = !!i.route && url.startsWith(i.route);

            const matchesAlternate = i.matchRoutes?.some((r) => url.startsWith(r)) ?? false;

            i.active = (matchesMainRoute || matchesAlternate) && !i.children?.length;

            if (i.children?.length) {
                this.markActiveRoute(i.children);

                const hasActiveChild = i.children.some((child) => child.active);
                if (hasActiveChild) {
                    i.open = true;
                }
            }
        }
        this.menuItems.update((items) => [...items]);
    }

    canAccess(permissionString: string): boolean {
        return this.currentUser?.permissions?.includes(permissionString) ?? false;
    }

    hasRole(roleString: string): boolean {
        return this.currentUser?.current_role == roleString;
    }

    hasAnyRole(roles: string[]): boolean {
        return roles.some((role) => this.hasRole(role));
    }

    hasAllRoles(roles: string[]): boolean {
        return roles.every((role) => this.hasRole(role));
    }

    filterMenuItems(items: SidebarMenuItem[]): SidebarMenuItem[] {
        return items
            .filter((item) => item.canAccess)
            .map((item) => {
                if (item.children?.length) {
                    const children = this.filterMenuItems(item.children);
                    // Only include parent if it has children after filtering
                    if (children.length > 0) {
                        return { ...item, children };
                    }
                    // If no children, only include parent if it has a route
                    return item.route ? { ...item, children: [] } : null;
                }
                // Just return the item
                return item;
            })
            .filter((item): item is SidebarMenuItem => !!item);
    }

    setMenuItemsByUserType(userType: string | undefined) {
        if (getUpperUserType(userType) === UserType.INTERNAL) {
            return signal<SidebarMenuItem[]>(this.filterMenuItems(this.internalMenuItems()));
        } else if (getUpperUserType(userType) === UserType.EXTERNAL) {
            return signal<SidebarMenuItem[]>(this.filterMenuItems(this.externalMenuItems()));
        } else {
            return signal<SidebarMenuItem[]>([
                {
                    label: 'Home',
                    active: false,
                    canAccess: true,
                    route: '/home',
                    icon: House,
                    children: [],
                },
            ]);
        }
    }
}
